import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Sparkles, TrendingUp, Rocket, Check, Menu, Zap, Mail, Users, Bot } from "lucide-react";

const ICONS = { free: Sparkles, pro: TrendingUp, scale: Rocket };
const ACCENTS = { free: "#64748B", pro: "#0044FF", scale: "#FF4D00" };

export default function Pricing() {
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [authed, setAuthed] = useState(!!localStorage.getItem("ld_token"));

  useEffect(() => {
    api.get("/billing/plans/public").then(({ data }) => setPlans(data.plans)).catch(() => {});
  }, []);

  const cta = (planId) => {
    if (planId === "free") return authed ? nav("/") : nav("/signup");
    if (!authed) { nav(`/signup?plan=${planId}`); return; }
    nav(`/billing`);
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }} data-testid="pricing-page">
      {/* Nav */}
      <header className="border-b sticky top-0 z-10" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md flex items-center justify-center text-white" style={{ background: "var(--brand)" }}><Sparkles className="w-4 h-4" /></div>
            <span className="display text-xl">LaunchDeck</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <a href="#features" className="hover:opacity-80">Features</a>
            <a href="#faq" className="hover:opacity-80">FAQ</a>
            {authed ? (
              <Button asChild size="sm" className="text-white" style={{ background: "var(--brand)" }}><Link to="/" data-testid="pricing-nav-dashboard">Dashboard</Link></Button>
            ) : (
              <>
                <Link to="/signin" className="hover:opacity-80">Sign in</Link>
                <Button asChild size="sm" className="text-white" style={{ background: "var(--brand)" }}><Link to="/signup" data-testid="pricing-nav-signup">Start free</Link></Button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto text-center px-6 pt-20 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-6" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)", color: "var(--brand)" }}>
          <Zap className="w-3 h-3" /> AI-POWERED MARKETING OS
        </div>
        <h1 className="display text-5xl md:text-7xl leading-tight mb-4">
          One tool. Every channel.<br />
          <span style={{ color: "var(--brand)" }}>Zero fluff.</span>
        </h1>
        <p className="text-lg mb-8 max-w-2xl mx-auto" style={{ color: "var(--text-soft)" }}>
          Email marketing, drag-drop templates, AI-generated newsletters + blogs, smart inbox with tone classification, social scheduling. All in one workspace — priced for founders.
        </p>
        <div className="inline-flex rounded-full p-1 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <button data-testid="pricing-cycle-monthly" onClick={() => setCycle("monthly")} className="px-5 py-1.5 text-sm rounded-full transition-all" style={{ background: cycle === "monthly" ? "var(--brand)" : "transparent", color: cycle === "monthly" ? "var(--on-brand)" : "var(--text-soft)" }}>Monthly</button>
          <button data-testid="pricing-cycle-yearly" onClick={() => setCycle("yearly")} className="px-5 py-1.5 text-sm rounded-full transition-all" style={{ background: cycle === "yearly" ? "var(--brand)" : "transparent", color: cycle === "yearly" ? "var(--on-brand)" : "var(--text-soft)" }}>Yearly <span className="text-xs opacity-80">· save 20%</span></button>
        </div>
      </section>

      {/* Plans */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map(p => {
            const Icon = ICONS[p.id] || Sparkles;
            const isFree = p.id === "free";
            const price = cycle === "yearly" ? Math.round(p.price_monthly * 12 * 0.8) : p.price_monthly;
            const suffix = cycle === "yearly" ? "/yr" : "/mo";
            const isPopular = p.id === "pro";
            return (
              <Card key={p.id} data-testid={`public-plan-${p.id}`} className={`p-6 relative ${isPopular ? "ring-2 -translate-y-2" : ""}`} style={{ background: "var(--surface)", borderColor: "var(--border)", boxShadow: isPopular ? `0 20px 40px -20px ${ACCENTS[p.id]}55` : undefined, ["--tw-ring-color"]: ACCENTS[p.id] }}>
                {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: ACCENTS[p.id] }}>Most popular</div>}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${ACCENTS[p.id]}15` }}>
                    <Icon className="w-5 h-5" style={{ color: ACCENTS[p.id] }} />
                  </div>
                  <div className="display text-2xl">{p.name}</div>
                </div>
                <div className="mb-4">
                  <span className="display text-5xl">${price}</span>
                  <span className="text-sm ml-1" style={{ color: "var(--text-soft)" }}>{isFree ? "forever" : suffix}</span>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ACCENTS[p.id] }} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button data-testid={`public-cta-${p.id}`} onClick={() => cta(p.id)} className="w-full text-white" style={{ background: ACCENTS[p.id] }}>
                  {isFree ? "Start free" : `Choose ${p.name}`}
                </Button>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 pb-24">
        <h2 className="display text-4xl text-center mb-3">Everything you need to grow</h2>
        <p className="text-center mb-12" style={{ color: "var(--text-soft)" }}>Replace 6 tools with one — priced for founders who ship.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { Icon: Mail,  title: "Email marketing",  desc: "Real Brevo delivery, per-recipient tracking, drag-drop templates" },
            { Icon: Bot,   title: "AI everywhere",    desc: "Gemini newsletters, blogs, images, tone classification, reply drafts" },
            { Icon: Users, title: "Smart contacts",   desc: "CSV smart import, tags, smart lists, per-contact timeline" },
            { Icon: Zap,   title: "Automations",      desc: "Triggers, delays, multi-step actions — no code required" },
          ].map(({ Icon, title, desc }) => (
            <Card key={title} className="p-5" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: "color-mix(in srgb, var(--brand) 12%, transparent)" }}>
                <Icon className="w-5 h-5" style={{ color: "var(--brand)" }} />
              </div>
              <div className="font-semibold mb-1">{title}</div>
              <div className="text-xs" style={{ color: "var(--text-soft)" }}>{desc}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 pb-24">
        <h2 className="display text-4xl text-center mb-8">FAQ</h2>
        <div className="space-y-4">
          {[
            { q: "Can I use my own domain for sending?", a: "Yes. Every plan includes sender-domain verification (SPF/DKIM/DMARC) so emails come from your brand." },
            { q: "How does the free plan compare to competitors?", a: "500 contacts and 1,000 emails a month — no credit card. Enough to validate a full launch before you pay a dollar." },
            { q: "Can I switch or cancel anytime?", a: "Yes. Cancel-at-period-end from Billing. Your data stays for 30 days if you want to reactivate." },
            { q: "Do you support teams?", a: "Pro includes 5 seats, Scale 25. Invite anyone by email — they get a magic link and a workspace role." },
          ].map((it) => (
            <details key={it.q} className="p-4 rounded-lg border cursor-pointer" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <summary className="font-semibold">{it.q}</summary>
              <p className="text-sm mt-2" style={{ color: "var(--text-soft)" }}>{it.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="border-t py-8 text-center text-xs" style={{ borderColor: "var(--border)", color: "var(--text-soft)" }}>
        LaunchDeck — Marketing OS for founders © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
